/**
 * Normalizers for ECL row fields.
 *
 * The shape we send to the backend is fixed by `offlineConversions.ts`.
 * These helpers turn raw CSV cells or form input into that shape.
 */

/**
 * Parse a user-entered timestamp string and re-emit it as RFC 3339 UTC with
 * a `Z` suffix — the format Google's Data Manager API expects.
 *
 * Returns null if the input is unparseable (the form / CSV path treats null
 * as a validation error to surface).
 */
export function toRfc3339Utc(input: string): string | null {
  if (!input) return null;
  const ts = new Date(input);
  if (Number.isNaN(ts.getTime())) return null;
  return ts.toISOString(); // ISO is RFC 3339 compatible
}

/**
 * Build a stable transaction_id when the CSV row omitted one.
 *
 * Google dedupes by (transaction_id, conversion_action_id) — if every row
 * has a stable ID, resubmits after a failure don't double-count. The seed
 * is the email + RFC-3339 timestamp + conversion action; same inputs yield
 * the same ID on a resubmit. Hashed for compactness + privacy (the literal
 * email never appears in the ID).
 */
export async function deriveTransactionId(
  seed: { email?: string | null; phone?: string | null; event_timestamp: string },
  conversionActionId: string
): Promise<string> {
  const enc = new TextEncoder();
  const text = [
    seed.email ?? '',
    seed.phone ?? '',
    seed.event_timestamp,
    conversionActionId,
  ].join('|');
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(text));
  return [...new Uint8Array(digest)]
    .slice(0, 16)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function normalizeCurrency(input: string): string | null {
  const trimmed = input.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(trimmed) ? trimmed : null;
}

export function parseConversionValue(input: string): number | null {
  if (!input || !input.trim()) return null;
  const n = Number(input.trim());
  return Number.isFinite(n) && n >= 0 ? n : null;
}
